import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "../api/axiosConfig";
import Navbar from "../components/Navbar";
import CreatableSelect from "react-select/creatable";

export default function ProductForm() {
  const { id } = useParams();
  const navigate = useNavigate();

  // Refs
  const displayImageRef = useRef(null);
  const galleryInputRef = useRef(null);

  // State
  const [form, setForm] = useState({
    model: "", partNumber: "", storage: "", ox: "", nki: "",
    price: "", od: "", id: "", height: ""
  });

  const [displayImageFile, setDisplayImageFile] = useState(null);
  const [currentDisplayImage, setCurrentDisplayImage] = useState("");
  
  const [files, setFiles] = useState([]);
  const [existingFiles, setExistingFiles] = useState([]);

  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [modelOptions, setModelOptions] = useState([]);

  useEffect(() => {
    // Fetch Models
    const fetchModels = async () => {
      try {
        const { data } = await axios.get("/models");
        const options = data.map(m => ({ value: m.name, label: m.name }));
        setModelOptions(options);
      } catch (err) {
        console.error("Failed to load models");
      }
    };
    fetchModels();

    if (id) {
      setIsEditMode(true);
      fetchProductDetails(id);
    }
  }, [id]);

  const fetchProductDetails = async (productId) => {
    try {
      const { data } = await axios.get(`/products/${productId}`);
      const { files: serverImages, displayImage, ...textData } = data;
      setForm(textData);
      if (serverImages) setExistingFiles(serverImages);
      if (displayImage) setCurrentDisplayImage(displayImage);
    } catch (error) {
      console.error("Error fetching product:", error);
    }
  };

  const handleInputChange = (e, field) => {
    setForm({ ...form, [field]: e.target.value });
  };

  const handleCreateOption = async (inputValue) => {
    setLoading(true);
    try {
        const { data } = await axios.post("/models/add", { name: inputValue });
        const newOption = { value: data.name, label: data.name };
        setModelOptions((prev) => [...prev, newOption]);
        setForm((prev) => ({ ...prev, model: newOption.value }));
    } catch (error) {
        alert("Failed to create model.");
    } finally {
        setLoading(false);
    }
  };

  const handleDisplayImageSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      setDisplayImageFile(e.target.files[0]);
    }
  };

  const handleGallerySelect = (e) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    setFiles((prev) => [...prev, selected]);
    setTimeout(() => (e.target.value = null), 0);
  };

  const removeNewFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const removeExistingImage = (index) => {
    setExistingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const submitProduct = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const fd = new FormData();
      Object.keys(form).forEach((key) => {
        if (key !== '_id' && key !== '__v' && key !== 'createdAt' && key !== 'updatedAt') {
          fd.append(key, form[key]);
        }
      });
      if (displayImageFile) fd.append("displayImage", displayImageFile);
      files.forEach((file) => fd.append("files", file));

      if (isEditMode) {
        fd.append("existingFiles", JSON.stringify(existingFiles));
        await axios.put(`/products/${id}`, fd);
      } else {
        await axios.post("/products/add", fd);
      }
      navigate("/");
    } catch (error) {
      alert("Failed to save.");
    } finally {
      setLoading(false);
    }
  };

  const formatLabel = (str) => str.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase());
  const textFields = ["storage", "partNumber", "ox", "nki"];
  const numberFields = ["price", "od", "id", "height"];
  const renderImageSrc = (path) => path.startsWith('http') ? path : `http://localhost:5000/${path.replace(/\\/g, '/')}`;

  return (
    <>
      <Navbar />
      <div className="container py-5 animate-fade-in">
        <div className="row justify-content-center">
          <div className="col-lg-8">
            <div className="card shadow-lg border-0 overflow-hidden">
              <div className="card-header bg-primary p-4">
                <h4 className="mb-0 text-center fw-bold !text-white">{isEditMode ? "Edit Product" : "New Product"}</h4>
              </div>
              <div className="card-body p-5">
                <form onSubmit={submitProduct}>

                  {/* --- DISPLAY IMAGE --- */}
                  <div className="mb-5 text-center">
                    <label className="form-label fw-bold text-muted small text-uppercase">Main Image</label>
                    <div 
                        className="mb-3 mx-auto d-flex justify-content-center align-items-center bg-light rounded border border-2 border-dashed" 
                        style={{ height: '200px', width: '100%', maxWidth: '300px', cursor: 'pointer' }}
                        onClick={() => displayImageRef.current.click()}
                    >
                      {displayImageFile ? (
                        <img src={URL.createObjectURL(displayImageFile)} alt="Preview" style={{ maxHeight: '100%', maxWidth: '100%' }} />
                      ) : currentDisplayImage ? (
                        <img src={renderImageSrc(currentDisplayImage)} alt="Current" style={{ maxHeight: '100%', maxWidth: '100%' }} />
                      ) : (
                        <div className="text-muted"><i className="bi bi-camera fs-2 d-block"></i>Click to Upload</div>
                      )}
                    </div>
                    <input type="file" className="d-none" ref={displayImageRef} onChange={handleDisplayImageSelect} />
                  </div>

                  {/* --- MODEL --- */}
                  <div className="row g-4 mb-4">
                    <div className="col-md-6">
                        <label className="form-label fw-bold text-muted small text-uppercase">Model</label>
                        <CreatableSelect
                            isClearable
                            isSearchable
                            isDisabled={loading}
                            isLoading={loading}
                            onChange={(newValue) => setForm({ ...form, model: newValue ? newValue.value : "" })}
                            onCreateOption={handleCreateOption}
                            options={modelOptions}
                            value={modelOptions.find(opt => opt.value === form.model) || null}
                            placeholder="Select or Type..."
                            className="basic-multi-select"
                            classNamePrefix="select"
                        />
                    </div>

                    {/* --- FIELDS --- */}
                    {textFields.map((field) => (
                      <div className="col-md-6" key={field}>
                        <label className="form-label fw-bold text-muted small text-uppercase">{formatLabel(field)}</label>
                        <input type="text" className="form-control" value={form[field] || ""} onChange={(e) => handleInputChange(e, field)} />
                      </div>
                    ))}
                    {numberFields.map((field) => (
                      <div className="col-md-6" key={field}>
                        <label className="form-label fw-bold text-muted small text-uppercase">{formatLabel(field)}</label>
                        <input type="number" className="form-control" value={form[field] || ""} onChange={(e) => handleInputChange(e, field)} />
                      </div>
                    ))}
                  </div>

                  {/* --- GALLERY --- */}
                  <div className="mb-4">
                    <label className="form-label fw-bold text-muted small text-uppercase d-block mb-3">Gallery</label>
                    
                    {/* Existing */}
                    {isEditMode && existingFiles.length > 0 && (
                        <div className="d-flex flex-wrap gap-2 mb-3">
                        {existingFiles.map((imgUrl, index) => (
                            <div key={index} className="position-relative border p-1 rounded bg-white shadow-sm">
                            <img src={renderImageSrc(imgUrl)} alt="gallery" style={{ width: '60px', height: '60px', objectFit: 'cover' }} className="rounded" />
                            <button type="button" className="btn btn-danger btn-sm position-absolute top-0 start-100 translate-middle rounded-circle p-0 d-flex align-items-center justify-content-center" style={{ width: '20px', height: '20px' }} onClick={() => removeExistingImage(index)}>&times;</button>
                            </div>
                        ))}
                        </div>
                    )}

                    {/* New List */}
                    {files.length > 0 && (
                        <ul className="list-group mb-3">
                        {files.map((file, index) => (
                            <li className="list-group-item d-flex justify-content-between align-items-center" key={index}>
                            <small>{file.name}</small>
                            <button type="button" className="btn btn-sm btn-outline-danger border-0" onClick={() => removeNewFile(index)}><i className="bi bi-x-lg"></i></button>
                            </li>
                        ))}
                        </ul>
                    )}

                    <input type="file" className="d-none" ref={galleryInputRef} onChange={handleGallerySelect} />
                    <button type="button" className="btn btn-outline-primary w-100 border-dashed" onClick={() => galleryInputRef.current.click()}>
                        <i className="bi bi-plus-circle me-2"></i> Add More Photos
                    </button>
                  </div>

                  <div className="d-grid mt-5">
                    <button type="submit" className="btn btn-primary btn-lg py-3" disabled={loading}>
                      {loading ? "Processing..." : isEditMode ? "Save Changes" : "Create Product"}
                    </button>
                  </div>

                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}